import { baseImageCovert, baseImageCovertForLogoPin } from "../../products/baseImageConver";
import { orderMediaByView } from "../../products/steps/mediaViews";
import { parseProcessStepsFromApi } from "../../products/ProcessStepsSection";
import {
  getFlexibleFirstProcesses,
  isFlexibleProcessWorkflow,
  mapLegacyProcessFormFields,
} from "../../products/productProcessMigrationUtils";

function normalizeThumbnailMedia(raw: any): any[] {
  const media = raw?.thumbnailDetails?.media;
  if (!Array.isArray(media)) return [];
  return media.map((m: any) => ({
    ...m,
    mediaURL: m?.mediaURL ?? m?.mediaUrl,
    show: m?.show,
    pin: m?.pin,
  }));
}

function legacyThumbnailRow(editDetails: any) {
  const t = editDetails?.thumbnail;
  if (t == null) return null;
  const url = typeof t === "string" ? t.trim() : "";
  if (!url) return null;
  return [
    {
      mediaURL: url,
      show: 1,
      pin: 1,
    },
  ];
}

/**
 * Maps API product document to the shape expected by {@link CatalogueProductInfo}.
 */
export function mapProductToDetailForm(editDetails: any, weavingCenterFallback?: string) {
  if (!editDetails) {
    return {};
  }
  const variantArry: any[] = [];
  const variantBaseDetails = {
    color: editDetails?.color,
    additionalImages: editDetails?.additionalImages?.length
      ? editDetails?.additionalImages
      : [],
  };
  variantArry.push(variantBaseDetails);
  const processFields = mapLegacyProcessFormFields(editDetails, baseImageCovert);
  const flexibleWorkflow = isFlexibleProcessWorkflow(editDetails);

  return {
    // Public catalogue responses may omit Mongo id; keep a stable key for UI + commerce.
    _id: editDetails?._id || editDetails?.id || editDetails?.productUniqueId,
    org: editDetails?.organizations?.length
      ? editDetails?.organizations[0]?.name
      : "",
    productOwner: editDetails?.productOwner,
    productUniqueId: editDetails?.productUniqueId,
    parentId: editDetails?.parentId,
    orgSrNo: editDetails?.orgSrNo,
    family: Array.isArray(editDetails?.family) ? editDetails.family : [],
    artform: editDetails?.artform?.name,
    type: editDetails?.type?.name,
    category: editDetails?.category?.name,
    subCategory: editDetails?.subcategory?.name,
    weaveType: editDetails?.weavetype?.name,
    productName: editDetails?.name,
    ...processFields,
    profileURL: (() => {
      const normalized = normalizeThumbnailMedia(editDetails);
      if (normalized.length > 0) {
        return baseImageCovertForLogoPin(orderMediaByView(normalized));
      }
      const legacy = legacyThumbnailRow(editDetails);
      if (legacy) {
        return baseImageCovertForLogoPin(legacy);
      }
      return [];
    })(),
    tags: editDetails?.tags ? editDetails?.tags : "",
    variants:
      editDetails?.varients?.length > 0
        ? [...variantArry, ...editDetails?.varients]
        : [...variantArry],
    nameOfBrand: editDetails?.brand,
    nameOfWeaver: editDetails?.weaver,
    handloomMark: editDetails?.handloom === "true" ? true : false,
    handloom: editDetails?.handloom === "true",
    silkMark: editDetails?.silk === "true" ? true : false,
    zariUsed: { label: editDetails?.zari, value: editDetails?.purity },
    weightOfSaree: editDetails?.sareeWeight,
    weightOfZariUsed: editDetails?.zariWeight,
    purity: editDetails?.purity,
    madeInIndia: editDetails?.madeIn === "true" ? true : false,
    yarnType: editDetails?.yarnType,
    workingHours: editDetails?.workingHours,
    loomNum: editDetails?.loomNum,
    xuid: editDetails?.xuid,
    SrNo: editDetails?.srNo,
    weavingCenter: editDetails?.weavingCenter || weavingCenterFallback,
    typeOfSareeByDesign: editDetails?.typeOfSareeByDesign,
    productionTimeInMonths: editDetails?.productionTimeInMonths,
    color: editDetails?.color,
    sellPrice: editDetails?.sellPrice,
    gstRate: editDetails?.gstRate,
    costPrice: editDetails?.costPrice,
    inStock: editDetails?.inStock === true,
    status: editDetails?.status,
    designNum: editDetails?.designNum,
    processSteps: parseProcessStepsFromApi(editDetails?.processSteps),
    flexibleUploadProcesses: flexibleWorkflow && Array.isArray(editDetails?.flexibleUploadProcesses)
      ? editDetails.flexibleUploadProcesses
      : [],
    processWorkflowVersion: flexibleWorkflow ? "flexible" : "legacy",
    // Same process list the certificate page shows (legacy designing/dyeing/weaving/finishing
    // or flexible processes), with artisan, bio, photo and media.
    certificateProcesses: getFlexibleFirstProcesses(editDetails),
  };
}
